import { stationNameHasEnergyStorage } from '../../utils/openEmsEvPortBattery';

/** ~11 m grid — same as embed co-located ports on the home map. */
export const EVUA_KM220_MATCH_DECIMALS = 4;

/** Hide a roaming pin when a 220-km port of similar power is within this radius. */
export const EVUA_KM220_NEARBY_METERS = 50;

/**
 * Similar rated power: the higher kW may exceed the lower by at most this fraction.
 * 180 vs 180 matches; 180 vs 150 matches; 180 vs 80 does not.
 */
export const EVUA_KM220_SIMILAR_POWER_RATIO = 0.2;

const EARTH_RADIUS_M = 6371000;

export function powersAreSimilarKw(leftKw, rightKw, ratio = EVUA_KM220_SIMILAR_POWER_RATIO) {
  const left = Number(leftKw);
  const right = Number(rightKw);
  if (!(left > 0) || !(right > 0)) return false;
  const high = Math.max(left, right);
  const low = Math.min(left, right);
  return (high - low) / high <= ratio + 1e-9;
}

export function distanceMeters(lat1, lon1, lat2, lon2) {
  const toRad = degrees => (degrees * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function locationKeyFromLatLng(lat, lng, decimals = EVUA_KM220_MATCH_DECIMALS) {
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }
  return `${latitude.toFixed(decimals)},${longitude.toFixed(decimals)}`;
}

function portHasWebcam(port) {
  const url = port?.webcamUrl ?? port?.webcam_url;
  return typeof url === 'string' && url.trim().length > 0;
}

function portIsCharging(port) {
  return (
    port?.job?.state === 'IN_PROGRESS' ||
    (typeof port?.name === 'string' && (port.name.includes('used') || port.name.includes('IN_PROGRESS')))
  );
}

function portIsOffline(port) {
  return port?.online === false;
}

function mergeMatchEntry(existing, { port, portNumber, powerKw, hasWebcam, hasEnergyStorage, isCharging, isOffline }) {
  if (portNumber && !existing.portNumbers.includes(portNumber)) {
    existing.portNumbers.push(portNumber);
  }
  existing.hasWebcam = existing.hasWebcam || hasWebcam;
  existing.hasEnergyStorage = existing.hasEnergyStorage || hasEnergyStorage;
  existing.isCharging = existing.isCharging || isCharging;
  existing.isOffline = Boolean(existing.isOffline) && isOffline;
  if (powerKw > existing.powerKw) {
    existing.powerKw = powerKw;
    existing.port = port;
  }
  const deviceId = port?.deviceId != null ? String(port.deviceId).trim() : '';
  if (deviceId) {
    existing.deviceId = deviceId;
  }
  return existing;
}

/**
 * Index 220-km ports by rounded GPS so EV UA stations at the same place can reuse km220 pins.
 * Ports that share a deviceId (e.g. 629+636) are merged even when GPS cells differ slightly.
 * @returns {Map<string, { port: object, portNumbers: string[], deviceId?: string, hasWebcam: boolean, hasEnergyStorage: boolean, isCharging: boolean, isOffline: boolean, powerKw: number }>}
 */
export function buildKm220LocationIndex(ports, getPowerKw = () => 0) {
  const index = new Map();
  index.locations = [];
  const byDeviceId = new Map();
  if (!Array.isArray(ports)) {
    return index;
  }

  for (const port of ports) {
    const key = locationKeyFromLatLng(port?.latitude, port?.longitude);
    if (!key) {
      continue;
    }
    const portNumber = String(port?.number ?? '').trim();
    const powerKw = Number(getPowerKw(port)) || 0;
    const hasWebcam = portHasWebcam(port);
    const hasEnergyStorage = stationNameHasEnergyStorage(port?.name);
    const isCharging = portIsCharging(port);
    const isOffline = portIsOffline(port);
    const deviceId = port?.deviceId != null ? String(port.deviceId).trim() : '';
    const payload = { port, portNumber, powerKw, hasWebcam, hasEnergyStorage, isCharging, isOffline };

    let entry = (deviceId && byDeviceId.get(deviceId)) || index.get(key);
    if (!entry) {
      entry = {
        port,
        portNumbers: portNumber ? [portNumber] : [],
        hasWebcam,
        hasEnergyStorage,
        isCharging,
        isOffline,
        powerKw,
        deviceId: deviceId || undefined,
      };
      if (deviceId) {
        byDeviceId.set(deviceId, entry);
      }
    } else {
      mergeMatchEntry(entry, payload);
      if (deviceId) {
        byDeviceId.set(deviceId, entry);
      }
    }

    index.set(key, entry);
    index.locations.push({
      lat: Number(port.latitude),
      lng: Number(port.longitude),
      entry,
    });
  }

  return index;
}

/**
 * 220-km port within {@link EVUA_KM220_NEARBY_METERS} whose rated kW is similar to the EV UA station.
 * Exact same-cell matches stay on {@link findKm220MatchForEvuaPort}; this is the wider roaming dedupe.
 */
export function findNearbySimilarKm220Match(evuaPort, evuaPowerKw, index, radiusM = EVUA_KM220_NEARBY_METERS) {
  const locations = index?.locations;
  if (!Array.isArray(locations) || locations.length === 0) return null;
  if (!powersAreSimilarKw(evuaPowerKw, evuaPowerKw)) return null;

  const lat = Number(evuaPort?.lat ?? evuaPort?.latitude);
  const lng = Number(evuaPort?.lon ?? evuaPort?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  let best = null;
  let bestDistance = radiusM;
  for (const location of locations) {
    if (!powersAreSimilarKw(evuaPowerKw, location.entry?.powerKw)) continue;
    const distance = distanceMeters(lat, lng, location.lat, location.lng);
    if (distance <= bestDistance) {
      best = location.entry;
      bestDistance = distance;
    }
  }
  return best;
}

export function findKm220MatchForEvuaPort(evuaPort, index) {
  if (!index || typeof index.get !== 'function') {
    return null;
  }
  const key = locationKeyFromLatLng(evuaPort?.lat ?? evuaPort?.latitude, evuaPort?.lon ?? evuaPort?.longitude);
  if (!key) {
    return null;
  }
  return index.get(key) ?? null;
}
